import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";
import { Provider } from "react-redux";
import store from "./redux/store";

import Home from "./pages/Home";
import CertificateVerificationPage from "./pages/CertificateVerificationPage";
import OfficeOnly from "./pages/OfficeOnly";
import UnityLearningPage from "./pages/UnityLearningPage";
import TalentLenzPage from "./pages/TalentLenzPage";
import FleetCorePage from "./pages/FleetCorePage";
import CustomSystemsPage from "./pages/CustomSystemsPage";
import AboutAndUsPage from "./pages/AboutAndUsPage";
import WorkPassportPage from "./pages/WorkPassportPage";
import ContactAndUsPage from "./pages/ContactAndUsPage";
import { PRE_AUDIT_DEMO_BASE_ROUTE } from "./preAudit/demo/preAuditDemoConfig";

import HeroRegisterUser from "./components/heroManagementDashboard/systemUtils/HeroRegisterUser";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import SupplierRouteGuard from "./components/heroManagementDashboard/financialDashboard/suppliers/SupplierRouteGuard";
import FinancialRouteGuard from "./components/heroManagementDashboard/financialDashboard/FinancialRouteGuard";
import HeroLoginRegisterPage from "./pages/HeroLoginRegisterPage";
import OnlineRegistrationPageHero from "./components/heroManagementDashboard/signUpLogIn/OnlineRegistrationPageHero";
import LearnerAccount from "./components/heroManagementDashboard/LearnerAccount/LearnerAccount";
import SessionCheckIn from "./pages/SessionCheckIn";

import StepWelcome from "./components/heroManagementDashboard/registration/steps/StepWelcome";
import StepCompanySetup from "./components/heroManagementDashboard/registration/steps/StepCompanySetup";
import StepAddUserGroups from "./components/heroManagementDashboard/registration/steps/StepAddUserGroups";
import StepSystemPermissions from "./components/heroManagementDashboard/registration/steps/StepSystemPermissions";

import ProtectedRoute from "./authHandlers/ProtectedRoute";
import { LEGACY_HERO_DASHBOARD_ROLES } from "./financialDashboard/financialRoleCapabilities";
import ApplicantProcess from "./pages/ApplicantProcess";
import RegisterHeroUserConsole from "./components/heroManagementDashboard/registration/RegisterHeroUserConsole";

import LoadingCircle from "./components/utils/LoadingCircle";
import HeroRegisterManual from "./components/heroManagementDashboard/TPC_Group_Manual_Register/HeroRegisterManual";
import HeroLoginManual from "./components/heroManagementDashboard/TPC_Group_Manual_Register/HeroLoginManual";
import TPCLearnerLogin from "./components/heroManagementDashboard/TPC_Group_Manual_Register/TPCLearnerLogin";
import ApplicantRegisterManual from "./components/heroManagementDashboard/applicant/registrationLogin/ApplicantRegisterManual";
import TradeRegister from "./components/heroManagementDashboard/applicant/registrationLogin/TradeRegister";
import ApplicantLoginManual from "./components/heroManagementDashboard/applicant/registrationLogin/ApplicantLoginManual";
import TradeApplicationLogin from "./components/heroManagementDashboard/applicant/registrationLogin/TradeApplicationLogin";
import AIProfessionalApplicationLogin from "./components/heroManagementDashboard/applicant/registrationLogin/AIProfessionalApplicationLogin";
import EmailTemplatePreview from "./components/heroManagementDashboard/applicant/EmailTemplatePreview";
import AIWorkplaceRegister from "./components/heroManagementDashboard/aiWorkplace/AIWorkplaceRegister";
import AIWorkplaceLogin from "./components/heroManagementDashboard/aiWorkplace/AIWorkplaceLogin";
import AIWorkplaceWelcome from "./components/heroManagementDashboard/aiWorkplace/AIWorkplaceWelcome";
import AIWorkplaceDashboard from "./components/heroManagementDashboard/aiWorkplace/AIWorkplaceDashboard";
import PayClaim from "./components/heroManagementDashboard/claims/PayClaim";
import LearnerUnitProgress from "./components/heroManagementDashboard/studentAdministration/LearnerUnitProgress";
import ETQADashboard from "./components/heroManagementDashboard/etqa/ETQADashboard";
import AccessPendingPage from "./platformAccess/routes/AccessPendingPage";
import { AccessProvider } from "./platformAccess/auth/AccessProvider";
import AccessRoleSelectorPage from "./platformAccess/routes/AccessRoleSelectorPage";
import CentralLoginPage from "./platformAccess/routes/CentralLoginPage";
import PasswordResetPage from "./platformAccess/routes/PasswordResetPage";
import PlatformAdminRegisterPage from "./platformAccess/routes/PlatformAdminRegisterPage";
import ApplicationRegisterPage from "./platformAccess/routes/ApplicationRegisterPage";
import ApplicantIntakePage from "./platformAccess/components/ApplicantIntakePage";
import ApplicantWorkspace from "./platformAccess/components/ApplicantWorkspace";
import PlatformAdminRoute from "./platformAccess/auth/PlatformAdminRoute";
import ProductRoute from "./platformAccess/auth/ProductRoute";
import TenantAdminRegisterPage from "./platformAccess/routes/TenantAdminRegisterPage";
import TenantAdminRoute from "./platformAccess/auth/TenantAdminRoute";
import TenantMemberRoute from "./platformAccess/auth/TenantMemberRoute";
import TenantUserRegisterPage from "./platformAccess/routes/TenantUserRegisterPage";
import SystemsDashboardPage from "./platformAccess/routes/SystemsDashboardPage";
import AccessRefreshNotice from "./platformAccess/components/AccessRefreshNotice";

import PortalLogin from "./components/portal/PortalLogin";
import PortalRoleSelect from "./components/portal/PortalRoleSelect";
import FunderLogin from "./components/portal/FunderLogin";
import SupervisorLogin from "./components/portal/SupervisorLogin";
import FunderRegister from "./components/portal/FunderRegister";
import SupervisorRegister from "./components/portal/SupervisorRegister";
import SupervisorPortalDashboard from "./components/portal/SupervisorPortalDashboard";

const LearnerDashboard = lazy(() => import("./pages/LearnerDashboard"));
const FacilitatorDashboard = lazy(() =>
  import("./components/facilitatorDashboard/FacilitatorDashboard")
);
const FunderPortalDashboard = lazy(() =>
  import("./components/portal/FunderPortalDashboard")
);
const PlatformAdminDashboard = lazy(() =>
  import("./platformAccess/dashboards/platform/PlatformAdminDashboard")
);
const TenantAdminDashboard = lazy(() =>
  import("./platformAccess/dashboards/tenant/TenantAdminDashboard")
);

const FleetCoreRoutes = lazy(() =>
  import("./fleetCore/routes/FleetCoreRoutes")
);
const UnityProductRoutes = lazy(() => import("./unity"));
const TalentLenzWorkspaceRoutes = lazy(() => import("./talentLenz"));
const TalentLenzDirectAssessment = lazy(() => import("./talentLenz/TalentLenzDirectAssessment"));
const FinancialProductRoutes = lazy(() => import("./financialDashboard/FinancialProductRoutes"));
const PreAuditRoutes = lazy(() => import("./preAudit/PreAuditRoutes"));
const PreAuditDemo = lazy(() => import("./preAudit/demo/PreAuditDemo"));
const TalentLenzOpportunityUserRoute = lazy(() =>
  import("./talentLenz/TalentLenzOpportunityUserRoute")
);

const MarketingManagement = lazy(() =>
  import(
    "./components/heroManagementDashboard/marketing2/marketingDashboard"
  )
);

const MarketingV2Dashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/marketingV2/MarketingV2Dashboard"
  )
);

const HeroDashboardContainer = lazy(() =>
  import(
    "./components/heroManagementDashboard/main/HeroDashboardContainer"
  )
);

const AdminPortalContainer = lazy(() =>
  import(
    "./components/heroManagementDashboard/adminPortal/AdminPortalContainer"
  )
);

const FunderAdminDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/adminDashboard/FunderAdminDashboard"
  )
);

const CrosslinkReportDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/crosslink/CrosslinkReportDashboard"
  )
);

const ContentDisplayEdit = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/uiComponents/contentComponents/ContentDisplayEdit"
  )
);

const BaselineAssessmentPage = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/contentBuilderComponents/contentBuildComponents/contentParser/baselineAssessments/BaselineAssessmentPage"
  )
);

const BaselineAssessmentWrapper = lazy(() =>
  import(
    "./components/learningDashboard/BaselineAssessmentWrapper"
  )
);

const UPResearchAssistant = lazy(() =>
  import(
    "./components/heroManagementDashboard/upra/UPResearchAssistant"
  )
);

const ClaimsSystem = lazy(() =>
  import(
    "./components/heroManagementDashboard/claims/ClaimsSystem"
  )
);

const AdjudicateClaim = lazy(() =>
  import(
    "./components/heroManagementDashboard/claims/AdjudicateClaim"
  )
);

const ClaimsTableView = lazy(() =>
  import(
    "./components/heroManagementDashboard/claims/ClaimsTableView"
  )
);

const HeroOverview = lazy(() =>
  import(
    "./components/heroManagementDashboard/HeroOverview"
  )
);

const UsersDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/users/UsersDashboard"
  )
);

const RoleAccessDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/roleAccess/RoleAccessDashboard"
  )
);

const SuppliersDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/financialDashboard/suppliers/views/SuppliersDashboard"
  )
);

const FinancialDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/financialDashboard/FinancialDashboard"
  )
);

const FacilitatorsDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/studentAdministration/facilitatorsDashboard"
  )
);

const ProfessionalLearnersDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/studentAdministration/ProfessionalLearnersDashboard"
  )
);

const UnityLearningDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/UnityLearningDashboard"
  )
);

const UnityLearningDashboardConsole = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/UnityLearningDashboardConsole"
  )
);

const OpenAIWebSearchTestSubsystem = lazy(() =>
  import(
    "./components/heroManagementDashboard/testing/OpenAIWebSearchTestSubsystem"
  )
);

const MMSDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/mms/MMSDashboard"
  )
);

const AIWorkplaceManagement = lazy(() =>
  import(
    "./components/heroManagementDashboard/aiWorkplace/AIWorkplaceManagement"
  )
);

const ExplorePrograms = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/ExplorePrograms"
  )
);

const ExploreModules = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/ExploreModules"
  )
);

const ExploreComponents = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/ExploreComponents"
  )
);

const ExploreUnits = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/ExploreUnits"
  )
);

const ExploreConcepts = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/ExploreConcepts"
  )
);

const ApprovalLanding = lazy(() =>
  import(
    "./components/heroManagementDashboard/approvalGate/ApprovalLanding"
  )
);

const AIContentBuilder = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/contentBuilderComponents/AIContentBuilder"
  )
);

const UnitContentCreator = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/contentBuilderComponents/unitContentDisplay/UnitContentCreator"
  )
);

const EpirocDemoCV = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/contentBuilderComponents/programBuilder/epirocDemoCV"
  )
);

const BuildProgram = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/contentBuilderComponents/programBuilder/BuildProgram"
  )
);

const NotificationCenterDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/notificationsCenter/NotificationCenterDashboard"
  )
);

const ApplicantDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/TPCJobApplications/reviewDashboard/ApplicantDashboard"
  )
);

const ProgramFees = lazy(() =>
  import(
    "./components/heroManagementDashboard/ProgramFees/ProgramFees"
  )
);

const SimplifiedRegister = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/TPCJobApplications/SimplifiedRegister"
  )
);

const OpportunitySelect = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/TPCJobApplications/OpportunitySelect"
  )
);

const JobDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/TPCJobApplications/JobDashboard"
  )
);

const ReviewDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/bursaryLearners/reviewDashboard/reviewDasboard"
  )
);

const JobApplicantLogin = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/TPCJobApplications/JobApplicantLogin"
  )
);

const BrochureRequest = lazy(() =>
  import(
    "./components/heroManagementDashboard/unityLearning/unityLearningPlatform/leadsEmailBrochure/BrochureRequest"
  )
);

const BursaryRegister = lazy(() =>
  import(
    "./components/heroManagementDashboard/bursaryLearners/BursaryRegister"
  )
);

const BursaryLogin = lazy(() =>
  import(
    "./components/heroManagementDashboard/bursaryLearners/BursaryLogin"
  )
);

const BursaryDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/bursaryLearners/dashboard/BursaryDashboard"
  )
);

const LearnerApplicantsDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/learnerApplicants/learnerApplicantsDashboard"
  )
);

const InternsCalendarPage = lazy(() =>
  import(
    "./components/internsCalendar/InternsCalendarPage"
  )
);

const InternsCalendarViewOnly = lazy(() =>
  import(
    "./components/internsCalendar/InternsCalendarViewOnly"
  )
);

const ApplicantWelcome = lazy(() =>
  import(
    "./components/heroManagementDashboard/applicant/ApplicantWelcome"
  )
);

const IntakeBlockLanding = lazy(() =>
  import(
    "./components/heroManagementDashboard/studentAdministration/intakeBlockLanding"
  )
);

const VaimoProposalRegister = lazy(() =>
  import(
    "./components/heroManagementDashboard/vaimoProposals/VaimoProposalRegister"
  )
);

const VaimoProposalLogin = lazy(() =>
  import(
    "./components/heroManagementDashboard/vaimoProposals/VaimoProposalLogin"
  )
);

const VaimoProposalDashboard = lazy(() =>
  import(
    "./components/heroManagementDashboard/vaimoProposals/dashboard/VaimoProposalDashboard"
  )
);

const OpportunityRegister = lazy(() =>
  import(
    "./components/heroManagementDashboard/opportunity/OpportunityRegister"
  )
);

const TalentLenzOpportunityRegistration = lazy(() =>
  import("./talentLenz/TalentLenzOpportunityRegistration")
);

const OpportunityPage = lazy(() =>
  import(
    "./components/heroManagementDashboard/opportunity/OpportunityPage"
  )
);

const OpportunityManagement = lazy(() =>
  import(
    "./components/heroManagementDashboard/opportunity/OpportunityManagement"
  )
);

const RegistrationLayout = () => {
  return <Outlet />;
};

function MarketingManagementWrapper() {
  const location = useLocation();

  return (
    <MarketingManagement
      key={`marketing-${
        location.key || location.pathname
      }`}
    />
  );
}

function PageLoader({ label = "Loading..." }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#ffffff",
      }}
    >
      <LoadingCircle label={label} />
    </div>
  );
}

function CentralAccessBoundary() {
  return (
    <AccessProvider>
      <AccessRefreshNotice />
      <Outlet />
    </AccessProvider>
  );
}

function AppContent() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path={`${PRE_AUDIT_DEMO_BASE_ROUTE}/*`} element={
        <Suspense fallback={<PageLoader label="Loading Pre-Audit demo..." />}>
          <PreAuditDemo />
        </Suspense>
      } />
      <Route path="/verify-certificate/:certificateId" element={<CertificateVerificationPage />} />

      <Route element={<CentralAccessBoundary />}>
        <Route path="/login" element={<CentralLoginPage />} />
        <Route path="/reset-password" element={<PasswordResetPage />} />
        <Route
          path="/platform-admin/register"
          element={<PlatformAdminRegisterPage />}
        />
        <Route path="/application" element={<ApplicationRegisterPage />} />
        <Route path="/application/apply" element={<ApplicantIntakePage />} />
        <Route path="/workspace" element={<ApplicantWorkspace />} />
        <Route
          path="/tenant-admin/register"
          element={<TenantAdminRegisterPage />}
        />
        <Route
          path="/tenant-user/register"
          element={<TenantUserRegisterPage />}
        />
        <Route path="/access-pending" element={<AccessPendingPage />} />
        <Route path="/dashboard" element={<AccessRoleSelectorPage />} />
        <Route
          path="/systems"
          element={
            <TenantMemberRoute>
              <SystemsDashboardPage />
            </TenantMemberRoute>
          }
        />
        <Route
          path="/unity/*"
          element={
            <ProductRoute productKey="unityLearning">
              <Suspense fallback={<PageLoader label="Loading Unity Learning..." />}>
                <UnityProductRoutes />
              </Suspense>
            </ProductRoute>
          }
        />
        <Route
          path="/talentlenz/workspace/*"
          element={
            <ProductRoute productKey="talentLenz">
              <Suspense fallback={<PageLoader label="Loading TalentLenz..." />}>
                <TalentLenzWorkspaceRoutes />
              </Suspense>
            </ProductRoute>
          }
        />
        <Route
          path="/financialDashboard/*"
          element={
            <ProductRoute productKey="financialDashboard">
              <Suspense fallback={<PageLoader label="Loading Financial Dashboard..." />}>
                <FinancialProductRoutes />
              </Suspense>
            </ProductRoute>
          }
        />
        <Route
          path="/pre-audit/*"
          element={
            <ProductRoute productKey="preAudit">
              <Suspense fallback={<PageLoader label="Loading Pre-Audit..." />}>
                <PreAuditRoutes />
              </Suspense>
            </ProductRoute>
          }
        />
        <Route
          path="/talentlenz/opportunity-assessment"
          element={
            <Suspense fallback={<PageLoader label="Loading assessment..." />}>
              <TalentLenzDirectAssessment />
            </Suspense>
          }
        />
        <Route
          path="/opportunity-page"
          element={
            <ProductRoute productKey="talentLenz">
              <Suspense fallback={<PageLoader label="Loading opportunity..." />}>
                <TalentLenzOpportunityUserRoute>
                  <OpportunityPage />
                </TalentLenzOpportunityUserRoute>
              </Suspense>
            </ProductRoute>
          }
        />
        <Route
          path="/platform-admin/dashboard"
          element={
            <PlatformAdminRoute>
              <Suspense fallback={<PageLoader label="Loading platform administration..." />}>
                <PlatformAdminDashboard />
              </Suspense>
            </PlatformAdminRoute>
          }
        />
        <Route
          path="/tenant-admin/dashboard"
          element={
            <TenantAdminRoute>
              <Suspense fallback={<PageLoader label="Loading tenant administration..." />}>
                <TenantAdminDashboard />
              </Suspense>
            </TenantAdminRoute>
          }
        />
        <Route
          path="/fleetcore/*"
          element={
            <Suspense fallback={<PageLoader label="Loading FleetCore..." />}>
              <FleetCoreRoutes />
            </Suspense>
          }
        />
      </Route>

      <Route
        path="/unity-learning"
        element={<UnityLearningPage />}
      />

      <Route
        path="/talentlenz"
        element={<TalentLenzPage />}
      />

      <Route
        path="/fleet-core"
        element={<FleetCorePage />}
      />

      <Route
        path="/custom-systems"
        element={<CustomSystemsPage />}
      />

      <Route
        path="/about-andus"
        element={<AboutAndUsPage />}
      />

      <Route
        path="/workpassport"
        element={<WorkPassportPage />}
      />

      <Route
        path="/contactAndUs"
        element={<ContactAndUsPage />}
      />

      <Route
        path="/office-only"
        element={<OfficeOnly />}
      />

      <Route
        path="/portal-login"
        element={<PortalLogin />}
      />

      <Route
        path="/portal-role-select"
        element={<PortalRoleSelect />}
      />

      <Route
        path="/funder-login"
        element={<FunderLogin />}
      />

      <Route
        path="/supervisor-login"
        element={<SupervisorLogin />}
      />

      <Route
        path="/funder-register"
        element={<FunderRegister />}
      />

      <Route
        path="/supervisor-register"
        element={<SupervisorRegister />}
      />

      <Route
        path="/funder-dashboard"
        element={
          <Suspense fallback={<PageLoader label="Loading funder dashboard..." />}>
            <FunderPortalDashboard />
          </Suspense>
        }
      />

      <Route
        path="/supervisor-dashboard"
        element={<SupervisorPortalDashboard />}
      />

      <Route
        path="/heroregister"
        element={<HeroRegisterUser />}
      />

      <Route
        path="/unauthorized"
        element={<UnauthorizedPage />}
      />

      <Route
        path="/heroLoginRegister"
        element={<HeroLoginRegisterPage />}
      />

      <Route
        path="/heroRegisterOnlineUser"
        element={<OnlineRegistrationPageHero />}
      />

      <Route
        path="/hero-register-manual"
        element={<HeroRegisterManual />}
      />

      <Route
        path="/hero-login-manual"
        element={<HeroLoginManual />}
      />

      <Route
        path="/trade-login"
        element={<TPCLearnerLogin />}
      />

      <Route
        path="/trade-register"
        element={<TradeRegister />}
      />

      <Route
        path="/trade-application-login"
        element={<TradeApplicationLogin />}
      />

      <Route
        path="/ai-professional-application-login"
        element={<AIProfessionalApplicationLogin />}
      />

      <Route
        path="/applicant-register"
        element={<ApplicantRegisterManual />}
      />

      <Route
        path="/applicant-login-manual"
        element={<ApplicantLoginManual />}
      />

      <Route
        path="/email-template-preview"
        element={<EmailTemplatePreview />}
      />

      <Route
        path="/complete-bursary-profile"
        element={
          <Suspense fallback={<PageLoader />}>
            <ReviewDashboard />
          </Suspense>
        }
      />

      <Route
        path="/ai-workplace-register"
        element={<AIWorkplaceRegister />}
      />

      <Route
        path="/ai-workplace-login"
        element={<AIWorkplaceLogin />}
      />

      <Route
        path="/ai-workplace-welcome"
        element={<AIWorkplaceWelcome />}
      />

      <Route
        path="/learner-welcome-home"
        element={
          <Navigate
            to="/learner-dashboard"
            replace
          />
        }
      />

      <Route
        path="/session-checkin"
        element={<SessionCheckIn />}
      />

      <Route
        path="/opportunity"
        element={
          <Suspense fallback={<PageLoader />}>
            <SimplifiedRegister />
          </Suspense>
        }
      />

      <Route
        path="/TPCOxyrtbOpportuntiesRef"
        element={
          <Suspense fallback={<PageLoader />}>
            <SimplifiedRegister />
          </Suspense>
        }
      />

      <Route
        path="/opportunity-selector"
        element={
          <Suspense fallback={<PageLoader />}>
            <OpportunitySelect />
          </Suspense>
        }
      />

      <Route
        path="/opportunity-dashboard"
        element={
          <Suspense fallback={<PageLoader />}>
            <JobDashboard />
          </Suspense>
        }
      />

      <Route
        path="/brochurerequestCyberSecurity"
        element={
          <Suspense fallback={<PageLoader />}>
            <BrochureRequest />
          </Suspense>
        }
      />

      <Route
        path="/programme"
        element={<Navigate to="/" replace />}
      />

      <Route
        path="/jobApplicantLogin"
        element={
          <Suspense fallback={<PageLoader />}>
            <JobApplicantLogin />
          </Suspense>
        }
      />

      <Route
        path="/interns48"
        element={
          <Suspense fallback={<PageLoader />}>
            <InternsCalendarPage />
          </Suspense>
        }
      />

      <Route
        path="/interns48/view"
        element={
          <Suspense fallback={<PageLoader />}>
            <InternsCalendarViewOnly />
          </Suspense>
        }
      />

      <Route
        path="/register-hero/*"
        element={
          <ProtectedRoute>
            <RegistrationLayout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Navigate to="welcome" replace />
          }
        />

        <Route
          path="welcome"
          element={<StepWelcome />}
        />

        <Route
          path="company-setup"
          element={<StepCompanySetup />}
        />

        <Route
          path="add-user-groups"
          element={<StepAddUserGroups />}
        />

        <Route
          path="system-permissions"
          element={<StepSystemPermissions />}
        />
      </Route>

      <Route
        path="/applicant/*"
        element={
          <ProtectedRoute
            allowedRoles={["applicant"]}
          >
            <ApplicantProcess />
          </ProtectedRoute>
        }
      />

      <Route
        path="/learner-dashboard/*"
        element={
          <ProtectedRoute
            allowedRoles={[
              "learner",
              "applicant",
              "freetrial",
              "bursaryLearner",
            ]}
            loginRoute="/applicant-login-manual"
          >
            <Suspense fallback={<PageLoader label="Loading learner dashboard..." />}>
              <LearnerDashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/baseline-assessment"
        element={
          <ProtectedRoute
            allowedRoles={[
              "learner",
              "applicant",
              "freetrial",
              "bursaryLearner",
            ]}
            loginRoute="/applicant-login-manual"
          >
            <Suspense fallback={<PageLoader />}>
              <BaselineAssessmentWrapper />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="review-applicants-onedashtpxKKlw"
        element={
          <Suspense fallback={<PageLoader />}>
            <ReviewDashboard />
          </Suspense>
        }
      />

      <Route
        path="/facilitator-dashboard/*"
        element={
          <ProtectedRoute
            allowedRoles={["Facilitator"]}
          >
            <Suspense fallback={<PageLoader label="Loading facilitator dashboard..." />}>
              <FacilitatorDashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin-portal"
        element={
          <ProtectedRoute
            allowedRoles={["Admin"]}
            loginRoute="/hero-login-manual"
          >
            <Suspense
              fallback={
                <PageLoader label="Loading Admin Portal..." />
              }
            >
              <AdminPortalContainer />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/hero-dashboard/*"
        element={
          <ProtectedRoute
            allowedRoles={[...LEGACY_HERO_DASHBOARD_ROLES]}
          >
            <Suspense
              fallback={
                <PageLoader label="Loading dashboard..." />
              }
            >
              <HeroDashboardContainer />
            </Suspense>
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<PageLoader />}>
              <HeroOverview />
            </Suspense>
          }
        />

        <Route
          path="learnerUnityProgress"
          element={<LearnerUnitProgress />}
        />

        <Route
          path="facilitators"
          element={
            <Suspense fallback={<PageLoader />}>
              <FacilitatorsDashboard />
            </Suspense>
          }
        />

        <Route
          path="etqa"
          element={<ETQADashboard />}
        />

        <Route
          path="crosslink-reports"
          element={
            <Suspense fallback={<PageLoader />}>
              <CrosslinkReportDashboard />
            </Suspense>
          }
        />

        <Route
          path="upra"
          element={
            <Suspense fallback={<PageLoader />}>
              <UPResearchAssistant />
            </Suspense>
          }
        />

        <Route
          path="marketing"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingManagementWrapper />
            </Suspense>
          }
        />

        <Route
          path="marketing-v2"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingV2Dashboard />
            </Suspense>
          }
        />

        <Route
          path="marketing-v2/email-templates"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingV2Dashboard />
            </Suspense>
          }
        />

        <Route
          path="marketing-v2/leads"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingV2Dashboard />
            </Suspense>
          }
        />

        <Route
          path="marketing-v2/workflows"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingV2Dashboard />
            </Suspense>
          }
        />

        <Route
          path="marketing-v2/learner-tracking"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingV2Dashboard />
            </Suspense>
          }
        />

        <Route
          path="opportunities/*"
          element={
            <Suspense fallback={<PageLoader />}>
              <OpportunityManagement />
            </Suspense>
          }
        />

        <Route
          path="mms"
          element={
            <Suspense fallback={<PageLoader />}>
              <MMSDashboard />
            </Suspense>
          }
        />

        <Route
          path="crosslink"
          element={
            <Suspense fallback={<PageLoader />}>
              <CrosslinkReportDashboard />
            </Suspense>
          }
        />

        <Route
          path="admin-dashboard"
          element={
            <Suspense fallback={<PageLoader />}>
              <FunderAdminDashboard />
            </Suspense>
          }
        />

        <Route
          path="claims"
          element={
            <Suspense fallback={<PageLoader />}>
              <ClaimsTableView />
            </Suspense>
          }
        />

        <Route
          path="programFees"
          element={
            <Suspense fallback={<PageLoader />}>
              <ProgramFees />
            </Suspense>
          }
        />

        <Route
          path="claims/add"
          element={
            <Suspense fallback={<PageLoader />}>
              <ClaimsSystem />
            </Suspense>
          }
        />

        <Route
          path="claims/adjudicate/:claimID"
          element={
            <Suspense fallback={<PageLoader />}>
              <AdjudicateClaim />
            </Suspense>
          }
        />

        <Route
          path="claims/pay/:claimID"
          element={
            <Suspense fallback={<PageLoader />}>
              <PayClaim />
            </Suspense>
          }
        />

        <Route
          path="applicants"
          element={
            <Suspense fallback={<PageLoader />}>
              <ApplicantWelcome />
            </Suspense>
          }
        />

        <Route
          path="baseline-assessment"
          element={
            <Suspense fallback={<PageLoader />}>
              <BaselineAssessmentPage />
            </Suspense>
          }
        />

        <Route
          path="learners-account"
          element={
            <Suspense fallback={<PageLoader />}>
              <LearnerAccount />
            </Suspense>
          }
        />

        <Route
          path="job-applicant-explore"
          element={
            <Suspense fallback={<PageLoader />}>
              <ApplicantDashboard />
            </Suspense>
          }
        />

        <Route
          path="intake-block"
          element={
            <Suspense fallback={<PageLoader />}>
              <IntakeBlockLanding />
            </Suspense>
          }
        />

        <Route
          path="intake-blocks"
          element={
            <Suspense fallback={<PageLoader />}>
              <IntakeBlockLanding />
            </Suspense>
          }
        />

        <Route
          path="program-fees"
          element={
            <Suspense fallback={<PageLoader />}>
              <ProgramFees />
            </Suspense>
          }
        />

        <Route
          path="up-research"
          element={
            <Suspense fallback={<PageLoader />}>
              <UPResearchAssistant />
            </Suspense>
          }
        />

        <Route
          path="bursary-learners"
          element={
            <Suspense fallback={<PageLoader />}>
              <ReviewDashboard />
            </Suspense>
          }
        />

        <Route
          path="job-applicants"
          element={
            <Suspense fallback={<PageLoader />}>
              <ApplicantDashboard />
            </Suspense>
          }
        />

        <Route
          path="users"
          element={
            <Suspense fallback={<PageLoader />}>
              <UsersDashboard />
            </Suspense>
          }
        />

        <Route
          path="role-access"
          element={
            <Suspense fallback={<PageLoader />}>
              <RoleAccessDashboard />
            </Suspense>
          }
        />

        <Route
          path="websearch-test"
          element={
            <Suspense fallback={<PageLoader />}>
              <OpenAIWebSearchTestSubsystem />
            </Suspense>
          }
        />

        <Route
          path="suppliers"
          element={
            <SupplierRouteGuard>
              <Suspense fallback={<PageLoader />}>
                <SuppliersDashboard />
              </Suspense>
            </SupplierRouteGuard>
          }
        />

        <Route
          path="financialDashboard"
          element={
            <FinancialRouteGuard>
              <Suspense fallback={<PageLoader />}>
                <FinancialDashboard />
              </Suspense>
            </FinancialRouteGuard>
          }
        />

        <Route
          path="learners"
          element={
            <Suspense fallback={<PageLoader />}>
              <ProfessionalLearnersDashboard />
            </Suspense>
          }
        />

        <Route
          path="unity-learning"
          element={
            <Suspense fallback={<PageLoader />}>
              <UnityLearningDashboardConsole />
            </Suspense>
          }
        >
          <Route
            path="view-content/:outcomeId"
            element={
              <Suspense fallback={<PageLoader />}>
                <ContentDisplayEdit />
              </Suspense>
            }
          />

          <Route
            path="create-concept"
            element={
              <Suspense fallback={<PageLoader />}>
                <EpirocDemoCV />
              </Suspense>
            }
          />

          <Route
            path="build-program"
            element={
              <Suspense fallback={<PageLoader />}>
                <BuildProgram />
              </Suspense>
            }
          />

          <Route
            path="explore-programs"
            element={
              <Suspense fallback={<PageLoader />}>
                <ExplorePrograms />
              </Suspense>
            }
          />

          <Route
            path="explore-modules"
            element={
              <Suspense fallback={<PageLoader />}>
                <ExploreModules />
              </Suspense>
            }
          />

          <Route
            path="explore-components"
            element={
              <Suspense fallback={<PageLoader />}>
                <ExploreComponents />
              </Suspense>
            }
          />

          <Route
            path="explore-units"
            element={
              <Suspense fallback={<PageLoader />}>
                <ExploreUnits />
              </Suspense>
            }
          />

          <Route
            path="explore-concepts"
            element={
              <Suspense fallback={<PageLoader />}>
                <ExploreConcepts />
              </Suspense>
            }
          />

          <Route
            path="learningTestSystem"
            element={
              <Suspense fallback={<PageLoader />}>
                <UnityLearningDashboard />
              </Suspense>
            }
          />
        </Route>

        <Route
          path="ai-workplace"
          element={
            <Suspense fallback={<PageLoader />}>
              <AIWorkplaceManagement />
            </Suspense>
          }
        />

        <Route
          path="approvalGateRegister"
          element={
            <Suspense fallback={<PageLoader />}>
              <ApprovalLanding />
            </Suspense>
          }
        />

        <Route
          path="notificationcenter"
          element={
            <Suspense fallback={<PageLoader />}>
              <NotificationCenterDashboard />
            </Suspense>
          }
        />

        <Route
          path="AIcontentBulder/:unitCode?"
          element={
            <Suspense fallback={<PageLoader />}>
              <AIContentBuilder />
            </Suspense>
          }
        />

        <Route
          path="unitContentCreator/:unitCode"
          element={
            <Suspense fallback={<PageLoader />}>
              <UnitContentCreator />
            </Suspense>
          }
        />

        <Route
          path="learner-applicants-dashboard"
          element={
            <Suspense fallback={<PageLoader />}>
              <LearnerApplicantsDashboard />
            </Suspense>
          }
        />
      </Route>

      <Route
        path="/registerHeroUserConsole"
        element={
          <ProtectedRoute>
            <RegisterHeroUserConsole />
          </ProtectedRoute>
        }
      />

      <Route
        path="/bursary-register"
        element={
          <Suspense fallback={<PageLoader />}>
            <BursaryRegister />
          </Suspense>
        }
      />

      <Route
        path="/bursary-login"
        element={
          <Suspense fallback={<PageLoader />}>
            <BursaryLogin />
          </Suspense>
        }
      />

      <Route
        path="/bursary_dashboard_learner"
        element={
          <ProtectedRoute
            allowedRoles={[
              "bursaryLearner",
              "bursaryLearner-TPC_Group-TechPerformanceCentre",
            ]}
          >
            <BursaryDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/vaimo-proposal-register"
        element={
          <Suspense fallback={<PageLoader />}>
            <VaimoProposalRegister />
          </Suspense>
        }
      />

      <Route
        path="/vaimo-proposal-login"
        element={
          <Suspense fallback={<PageLoader />}>
            <VaimoProposalLogin />
          </Suspense>
        }
      />

      <Route
        path="/opportunity-registration"
        element={
          <Suspense fallback={<PageLoader />}>
            <OpportunityRegister />
          </Suspense>
        }
      />

      <Route
        path="/talentlenz/opportunity-registration"
        element={
          <Suspense fallback={<PageLoader />}>
            <TalentLenzOpportunityRegistration />
          </Suspense>
        }
      />

      <Route
        path="/opportunity-login"
        element={<Navigate to="/login" replace />}
      />

      <Route
        path="/vaimo-proposal-dashboard"
        element={
          <ProtectedRoute
            allowedRoles={["vaimo-client-proposal"]}
          >
            <Suspense fallback={<PageLoader />}>
              <VaimoProposalDashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ai-workplace-dashboard"
        element={<AIWorkplaceDashboard />}
      />
    </Routes>
    
  );
}

function App() {
  return (
    <Provider store={store}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AppContent />
      </Router>
    </Provider>
  );
}

export default App;
